/* eslint-disable @typescript-eslint/no-unused-vars */
import axios, { AxiosError } from 'axios';
import { Environment } from '../../../../environment';
import { ApiFiscal } from '../../axiosfiscal';
import { v4 as uuidv4 } from 'uuid';
import { GeraArquivoLCDPR } from '../../../../../pages/fiscal';

export interface IListagemLCDPR {
  idlcdpr: string;
  dataemissao: Date;
  datacaixa: Date;
  produtor: string;
  participante: string;
  numero: string;
  entrada: number;
  saida: number;
}

export interface IDetalheProdutoNfe {
  idprodutor: string;
  idfazenda: string;
  idconta: string;
  datacaixa: Date;
  dataemissao: Date;
  numero: string;
  tipodoc: number;
  tipolan: number;
  idparceiro?: string | null;
  entrada: number;
  saida: number;
  historico: string
  idparticipante: string;
}

export interface IRReceita {
  vencimento: Date;
  numeronfe: string;
  nome: string;
  cpf: string;
  historico: string;
  valornfe: number;
  nferecebido: number;
  nfereceber: number;
}

export interface IRomaneios {
  vencimento: Date;
  numeronfe: string;
  numero: number;
  nome: string;
  cpf: string;
  valornfe: number;
}

export interface IDespesa {
  vencimento: Date;
  numeronfe: string;
  parcela: string;
  nome: string;
  cpf: string;
  historico: string;
  valornfe: number;
  nfepago: number;
  nfepagar: number;
}

export interface ICaixa {
  dataemissao: Date;
  datacaixa: Date; 
  numero: string; 
  banco: string; 
  participante: string;
  historico: string;
  entrada: number;
  saida: number;
}

export interface ILCDPR {
  dataemissao: Date;
  datacaixa: Date; 
  fazenda: string; 
  numero: string; 
  banco: string; 
  participante: string; 
  historico: string; 
  entrada: number; 
  saida: number; 
  saldo: number;
}

interface IlcdprConta{
  datacaixa: Date;
  numero: string;
  parceiro: string;
  cpf: string;
  historico: string;
  entrada: number;
  saida: number;
}

interface IlcdprParceiro{
  datacaixa: Date;
  numero: string;
  conta: string;
  historico: string;
  entrada: number;
  saida: number;
}

export interface ISaldoConta{
  conta: string;
  ir: string;
  entrada: number;
  saida: number;
  saldo: number;
}

export interface ISaldoFazenda{
  fazenda: string;
  ir: string;
  entrada: number;
  saida: number;
  saldo: number;
}

export interface IConferenciaNotas{
  dataemissao: Date;
  nome: string;
  cpf: string;
  historicolcdpr: string;
  numero: string;
  saida: number;
}


interface produtor {
  idprodutor: number;
	nome: string;
  entrada: number;
  saida: number;
}

type TotalCount = {
  data: IListagemLCDPR[];
  totalCount: number; 
}

interface ErrorResponse {
  error: {
    default: string;
  };
}

export interface IPorConta {
  produtor: produtor;
  lcdpr: IlcdprConta[];
}

export interface IPorFazendas {
  produtor: produtor;
  lcdpr: IlcdprConta[];
}

export interface IPorParceiro{
  produtor: produtor;
  lcdpr: IlcdprParceiro[];
}

interface ITerceiros{
  idir: string;
  tipocontraparte: number;
  cpf: string;
  nome: string;
  percentual: string;
}

interface IFazendas{
  idir: string;
  caditr: number;
  caepf: string;
  ie: string;
  descricao: string;
  endereco: string;
  numero: string;
  compl: string;
  bairro: string;
  uf: string;
  idmunicipio: string;
  cep: string;
  tipocontraparte: string;
  percentual: string;
  terceiros: ITerceiros[]
}

interface IProdutor {
  nome: string;
  cpf: string; 
  endereco: string
  numero: string
  complemento: string
  bairro: string
  uf: string
  idmunicipio: string
  cep: string
  numtel: string
  email: string
  fazendas: IFazendas[]
}

interface ILancamentos {
  datacaixa: string;
  idir: string;
  idconta: string;
  numero: string;
  tipodoc: string;
  historico: string;
  participante: string;
  tipolan: string;
  entrada: number;
  saida: number;
  saldo: number;
  letra: string;
}

interface IConta {
  idconta: string;
  banco: string;
  nomebanco: string;
  agencia: string;
  numeroconta: string;
}

interface IResumo {
  mes: string;
	ano: string;
	entrada: number;
	saida: number;
	saldo: number;
	letra: string;
}

export interface IGeraArquivo {
  produtor: IProdutor;
  fazendas: IFazendas[];
  contas: IConta[];
  lancamentos: ILancamentos[]; 
  resumo: IResumo[];
}


const getAll = async (page = 1, dataInicial = '', dataFinal='', produtor = '', numero = '', tipo=''): Promise<TotalCount | Error> => {
  try {

    const urlRelativa = `/lcdpr?page=${page}&limit=${Environment.LIMITE_DE_LINHAS}&tipo=${tipo}&produtor=${produtor}&numero=${numero}&datainicial=${dataInicial}&datafinal=${dataFinal}`;

    const { data, headers } = await ApiFiscal.get(urlRelativa);  
 
    if (data) {
      return {
        data,
        totalCount: Number(headers['x-total-count'] || Environment.LIMITE_DE_LINHAS),
      };
    }
 
    return new Error('Erro ao listar os registros.');
  } catch (error) {
    if (axios.isAxiosError(error)) {  
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const getAllList = async (filter = '', id = ''): Promise<TotalCount | Error> => {
  try {

    const urlRelativa: string = id === '' ? `/lcdprlist?limit=${Environment.LIMITE_DE_LINHAS}&tipo=descricao&filter=${filter}` : `/lcdprlist/${id}`;

    const { data, headers } = await ApiFiscal.get(urlRelativa);

    if (data) {
      return {
        data,
        totalCount: Number(headers['x-total-count'] || Environment.LIMITE_DE_LINHAS),
      };
    }
 
    return new Error('Erro ao listar os registros.');
  } catch (error) {
    if (axios.isAxiosError(error)) {  
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const getById = async (id: string): Promise<IDetalheProdutoNfe | Error> => {
  try {
    const { data } = await ApiFiscal.get(`/lcdpr/${id}`);

    if (data) {
      return data;
    }

    return new Error('Erro ao consultar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {  
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const create = async (dados: IDetalheProdutoNfe): Promise<string | Error> => {
  try {
    const guuid = uuidv4();
    const { data } = await ApiFiscal.post<IDetalheProdutoNfe>('/lcdpr',  {idlcdpr: guuid, ...dados});
  
    if (Object.keys(data).length === 0) {
      return guuid;
    }

    return new Error('Erro ao criar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {  
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const updateById = async (id: string, dados: IDetalheProdutoNfe): Promise<void | Error> => {
  try {
    await ApiFiscal.put(`/lcdpr/${id}`, dados);
  } catch (error) {
    if (axios.isAxiosError(error)) {  
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const deleteById = async (id: string): Promise<void | Error> => {
  try {
    await ApiFiscal.delete(`/lcdpr/${id}`);
  } catch (error) {
    if (axios.isAxiosError(error)) {  
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const RelatorioReceita = async (produtor = 0, dataInicial = '', dataFinal=''): Promise<IRReceita[] | Error> => {
  try {
    const urlRelativa = `/relatorioreceitadespesa?produtor=${produtor}&tipo=receita&datainicial=${dataInicial}&datafinal=${dataFinal}`;

    const { data } = await ApiFiscal.get(urlRelativa);
    if (data) {
      return data;
    }
    return new Error('Erro ao consultar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {  
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const RelatorioReceitaCitros = async (produtor = 0, dataInicial = '', dataFinal=''): Promise<IRReceita[] | Error> => {
  try {
    const urlRelativa = `/relatorioreceitadespesacitros?produtor=${produtor}&tipo=receita&datainicial=${dataInicial}&datafinal=${dataFinal}`;

    const { data } = await ApiFiscal.get(urlRelativa);
    if (data) {
      return data;
    }
    return new Error('Erro ao consultar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {  
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const RelatorioDespesa = async (produtor = 0, dataInicial = '', dataFinal=''): Promise<IDespesa[] | Error> => {
  try {
    const urlRelativa = `/relatorioreceitadespesa?produtor=${produtor}&tipo=despesa&datainicial=${dataInicial}&datafinal=${dataFinal}`;

    const { data } = await ApiFiscal.get(urlRelativa);
    if (data) {
      return data;
    }
    return new Error('Erro ao consultar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {  
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const RelatorioDespesaCitros = async (produtor = 0, dataInicial = '', dataFinal=''): Promise<IDespesa[] | Error> => {
  try {
    const urlRelativa = `/relatorioreceitadespesacitros?produtor=${produtor}&tipo=despesa&datainicial=${dataInicial}&datafinal=${dataFinal}`;

    const { data } = await ApiFiscal.get(urlRelativa);
    if (data) {
      return data;
    }
    return new Error('Erro ao consultar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {  
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const RelatorioRoamenios = async (produtor = 0, dataInicial = '', dataFinal=''): Promise<IRomaneios[] | Error> => {
  try {
    const urlRelativa = `/relatorioreceitadespesa?produtor=${produtor}&tipo=romaneios&datainicial=${dataInicial}&datafinal=${dataFinal}`;

    const { data } = await ApiFiscal.get(urlRelativa);
    if (data) {
      return data;
    }
    return new Error('Erro ao consultar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {  
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const RelatorioRoameniosCitros = async (produtor = 0, dataInicial = '', dataFinal=''): Promise<IRomaneios[] | Error> => {
  try {
    const urlRelativa = `/relatorioreceitadespesacitros?produtor=${produtor}&tipo=romaneios&datainicial=${dataInicial}&datafinal=${dataFinal}`;

    const { data } = await ApiFiscal.get(urlRelativa);
    if (data) {
      return data;
    }
    return new Error('Erro ao consultar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {  
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const RelatorioCaixa = async (produtor = 0, tipo = '', dataInicial = '', dataFinal=''): Promise<ICaixa[] | Error> => {
  try {
    const urlRelativa = `/relatoriolcdpr?produtor=${produtor}&tipo=${tipo}&datainicial=${dataInicial}&datafinal=${dataFinal}`;

    const { data } = await ApiFiscal.get(urlRelativa);
    if (data) {
      return data;
    }
    return new Error('Erro ao consultar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {  
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const RelatorioCaixaCitros = async (produtor = 0, tipo = '', dataInicial = '', dataFinal=''): Promise<ICaixa[] | Error> => {
  try {
    const urlRelativa = `/relatoriolcdprcitros?produtor=${produtor}&tipo=${tipo}&datainicial=${dataInicial}&datafinal=${dataFinal}`;

    const { data } = await ApiFiscal.get(urlRelativa);
    if (data) {
      return data;
    }
    return new Error('Erro ao consultar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {  
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const RelatorioLCDPR = async (produtor = 0, dataInicial = '', dataFinal=''): Promise<ILCDPR[] | Error> => {
  try {
    const urlRelativa = `/relatoriolcdpr?produtor=${produtor}&tipo=lcdpr&datainicial=${dataInicial}&datafinal=${dataFinal}`;

    const { data } = await ApiFiscal.get(urlRelativa);
    if (data) {
      return data;
    }
    return new Error('Erro ao consultar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {  
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const RelatorioLCDPRCitros = async (produtor = 0, dataInicial = '', dataFinal=''): Promise<ILCDPR[] | Error> => {
  try {
    const urlRelativa = `/relatoriolcdprcitros?produtor=${produtor}&tipo=lcdpr&datainicial=${dataInicial}&datafinal=${dataFinal}`;

    const { data } = await ApiFiscal.get(urlRelativa);
    if (data) {
      return data;
    }
    return new Error('Erro ao consultar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {  
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};


const RelatorioPorParceiro = async (idparceiro = '', dataInicial = '', dataFinal=''): Promise<IPorParceiro[] | Error> => {
  try {
    const urlRelativa = `/rlcdprporparceiro?parceiro=${idparceiro}&datainicial=${dataInicial}&datafinal=${dataFinal}`;

    const { data } = await ApiFiscal.get(urlRelativa);
    if (data) {
      return data;
    }
    return new Error('Erro ao consultar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {  
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const GeraArquivo = async (gera: boolean, idprodutor = 0, ano = 0): Promise<IGeraArquivo | Error> => {
  try {
    const urlRelativa = `/geraarquivo?gera=${gera}&idprodutor=${idprodutor}&ano=${ano}`;

    const { data } = await ApiFiscal.get(urlRelativa);
    if (data) {
      return data;
    }
    return new Error('Erro ao consultar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {  
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const RelatorioPorConta = async (idconta = 0, dataInicial = '', dataFinal = ''): Promise<IPorConta[] | Error> => {
  try {
    const urlRelativa = `/rlcdprporconta?conta=${idconta}&datainicial=${dataInicial}&datafinal=${dataFinal}`;

    const { data } = await ApiFiscal.get(urlRelativa);
    if (data) {
      return data;
    }
    return new Error('Erro ao consultar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {  
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const RelatorioSaldoConta = async (dataInicial = '', dataFinal = ''): Promise<ISaldoConta[] | Error> => {
  try {
    const urlRelativa = `/rlcdprcontas?datainicial=${dataInicial}&datafinal=${dataFinal}`;

    const { data } = await ApiFiscal.get(urlRelativa);
    if (data) {
      return data;
    }
    return new Error('Erro ao consultar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {  
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const RelatorioSaldoFazenda = async (dataInicial = '', dataFinal = ''): Promise<ISaldoFazenda[] | Error> => {
  try {
    const urlRelativa = `/rlcdprfazendas?datainicial=${dataInicial}&datafinal=${dataFinal}`;

    const { data } = await ApiFiscal.get(urlRelativa);
    if (data) {
      return data;
    }
    return new Error('Erro ao consultar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {  
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const RelatorioPorFazenda = async (idfazenda = '', dataInicial = '', dataFinal = ''): Promise<IPorFazendas[] | Error> => {
  try {
    const urlRelativa = `/rlcdprporfazendas?fazenda=${idfazenda}&datainicial=${dataInicial}&datafinal=${dataFinal}`;

    const { data } = await ApiFiscal.get(urlRelativa);
    if (data) {
      return data;
    }
    return new Error('Erro ao consultar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {  
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const RelatorioPorFazendaExport = async (idfazenda = '', dataInicial = '', dataFinal = ''): Promise<IlcdprConta[] | Error> => {
  try {
    const urlRelativa = `/rlcdprporfazendasexport?fazenda=${idfazenda}&datainicial=${dataInicial}&datafinal=${dataFinal}`;

    const { data } = await ApiFiscal.get(urlRelativa);
    if (data) {
      return data;
    }
    return new Error('Erro ao consultar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {  
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const RelatorioConferenciaNotas = async (idprodutor = 0, dataInicial = '', dataFinal = ''): Promise<IConferenciaNotas[] | Error> => {
  try {
    const urlRelativa = `/rconferencialcdpr?produtor=${idprodutor}&datainicial=${dataInicial}&datafinal=${dataFinal}`;

    const { data } = await ApiFiscal.get(urlRelativa);
    if (data) {
      return data;
    }
    return new Error('Erro ao consultar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {  
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.error?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

export const LCDPRService = {
  getAllList,
  getAll,
  getById,
  create,
  updateById,
  deleteById,
  RelatorioReceita,  
  RelatorioDespesa,
  RelatorioRoamenios,
  RelatorioCaixa,
  RelatorioLCDPR,
  RelatorioLCDPRCitros,
  RelatorioCaixaCitros,
  RelatorioPorConta,
  RelatorioSaldoConta,
  RelatorioPorParceiro,
  RelatorioPorFazenda,
  RelatorioSaldoFazenda,
  RelatorioConferenciaNotas,
  GeraArquivo,
  RelatorioReceitaCitros,
  RelatorioDespesaCitros,
  RelatorioRoameniosCitros,
  RelatorioPorFazendaExport,
};